<template>
  <a
    v-bind="$attrs"
    :href="href"
    :target="target"
    @click.capture="handleClick">
    <slot />
  </a>
</template>

<script setup lang="ts">
/**
 * This component replaces the NuxtLink component to expand with some missing features.
 * It is used to handle anchor links with smooth scrolling behavior.
 * It is also used to handle external links with a new tab.
 *
 * The href is resolved through the router instead of wrapping a `<NuxtLink custom>`:
 * that wrapper made this component's root a slot-rendering component, which broke
 * hydration wherever CustomNuxtLink itself sits on another component's root (see
 * FunctionalCard), duplicating the whole subtree.
 */
import { navigateTo, useRouter, type NuxtLinkProps } from 'nuxt/app'
import { computed, inject } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<{
  to: NuxtLinkProps['to']
  target?: string
}>()

const emit = defineEmits(['click'])

const closeAsyncPagePopup = inject<() => void>('closeAsyncPagePopup', () => {})

const isAnchorLink = (to: NuxtLinkProps['to']): to is string => {
  return typeof to === 'string' && to.startsWith('#')
}

const isExternalLink = (to: NuxtLinkProps['to']): to is string => {
  return typeof to === 'string' && (to.startsWith('http') || to.includes('mailto:') || to.includes('tel:'))
}

const router = useRouter()

const href = computed<string | undefined>(() => {
  const to = props.to

  if (to === null || to === undefined || to === '') {
    return undefined
  }

  // Protocol, protocol-relative and anchor links are used as-is, like NuxtLink does.
  if (typeof to === 'string' && (/^[a-z][a-z\d+.-]*:/i.test(to) || to.startsWith('//') || to.startsWith('#'))) {
    return to
  }

  try {
    return router.resolve(to).href
  }
  catch {
    return typeof to === 'string' ? to : undefined
  }
})

const handleClick = (event: MouseEvent) => {
  emit('click', event)

  if (event.metaKey || event.ctrlKey) {
    return
  }

  // If a target attribute is specified, let the browser handle it
  // `_self` is the browser default and carries no intent, so it must not opt out of the logic
  // below: doing so turns internal links into full page loads and breaks anchor scrolling.
  if (props.target && props.target !== '_self') {
    // Don't prevent default, let the browser use the target attribute
    return
  }

  if (event.defaultPrevented) {
    return
  }

  event.preventDefault()

  if (isExternalLink(props.to)) {
    // Only for external links without a target attribute, default to _blank
    window.open(props.to, '_blank')
    return
  }

  closeAsyncPagePopup?.()

  // Anchors go through the router as well, so the hash ends up in the URL and a single place
  // (`scrollBehavior`) decides how to scroll. Path and query are carried over by hand: handing
  // the router a bare `#hash` string would drop the query, wiping any active filters.
  if (isAnchorLink(props.to)) {
    const { path, query } = router.currentRoute.value
    navigateTo({ path, query, hash: props.to })

    return
  }

  navigateTo(props.to)
}
</script>
